
// ============================================================
// SQL CORE HELPERS (V40 — FIXED & FINAL)
// Deep Navigation + Recursive CTE + SubQuery + Apply
// ============================================================

// ============================================================
// LITERAL
// ============================================================
export class SqlLiteral {
  constructor(public value: any) { }
}

// ============================================================
// FUNCTION WRAPPER
// ============================================================
export class SqlFn {
  constructor(public field: any, public op: string, public arg?: any) { }

  toObject() {
    return { type: "Fn", op: this.op, field: this.field, arg: this.arg };
  }
}

// ============================================================
// SqlField — Deep Navigation Field
// ============================================================
export class SqlField {
  [key: string]: any;

  public __isField = true;
  public joinAlias: string;
  public navRootTable = "";
  public navRootAlias = "";

  constructor(
    public alias: string,
    public name: string,
    public navTableName: string = "",
    public navColumnName: string = "id",
    joinAliasOverride?: string
  ) {
    this.joinAlias =
      joinAliasOverride ?? `${alias}_${name.replace(/Id$/i, "")}`;

    this.navRootTable = navTableName || alias;
    this.navRootAlias = this.joinAlias;

    return new Proxy(this, deepFieldHandler);
  }

  eq(v: any) { return new SqlExp(this, "=", v); }
  ne(v: any) { return new SqlExp(this, "<>", v); }
  gt(v: any) { return new SqlExp(this, ">", v); }
  gte(v: any) { return new SqlExp(this, ">=", v); }
  lt(v: any) { return new SqlExp(this, "<", v); }
  lte(v: any) { return new SqlExp(this, "<=", v); }
  like(v: any) { return new SqlExp(this, "LIKE", v); }
  in(v: any[]) { return new SqlExp(this, "IN", v); }
  notIn(v: any[]) { return new SqlExp(this, "NOT IN", v); }
  isNull() { return new SqlExp(this, "IS NULL", null); }
  isNotNull() { return new SqlExp(this, "IS NOT NULL", null); }

  count() { return new SqlFn(this, "COUNT"); }
  sum() { return new SqlFn(this, "SUM"); }
  add(v: any) { return new SqlFn(this, "ADD", v); }

  asc() { return `${this.joinAlias}.${this.name} ASC`; }
  desc() { return `${this.joinAlias}.${this.name} DESC`; }

  literal(v: any) { return new SqlLiteral(v); }
}

// ============================================================
// Deep Proxy Handler
// ============================================================
const deepFieldHandler: ProxyHandler<SqlField> = {
  get(target, prop: string | symbol) {
    if (typeof prop !== "string") return undefined;
    if (prop in target) return (target as any)[prop];

    const deeper = new SqlField(
      target.joinAlias,
      prop,
      target.navRootTable,
      prop,
      target.joinAlias
    );

    return new Proxy(deeper, deepFieldHandler);
  }
};

// ============================================================
// SqlNavHybrid
// ============================================================
export class SqlNavHybrid {
  [key: string]: any;
  public __isNav = true;

  constructor(
    public alias: string,
    public joinAlias: string,
    public tableName: string
  ) {
    return new Proxy(this, navHandler);
  }

  asSqlField(): SqlField {
    return new SqlField(
      this.alias,
      `${this.tableName}Id`,
      this.tableName,
      "id",
      this.joinAlias
    );
  }
}

const navHandler: ProxyHandler<SqlNavHybrid> = {
  get(target, prop: string | symbol) {
    if (typeof prop !== "string") return undefined;
    if (prop in target) return (target as any)[prop];

    const f = new SqlField(
      target.joinAlias,
      prop,
      target.tableName,
      prop,
      target.joinAlias
    );

    return new Proxy(f, deepFieldHandler);
  }
};

export function createNavHybrid(
  alias: string,
  joinAlias: string,
  tableName: string
): SqlNavHybrid {
  return new Proxy(new SqlNavHybrid(alias, joinAlias, tableName), navHandler);
}

// ============================================================
// EXPRESSIONS
// ============================================================
export class SqlExp {
  constructor(
    public left: SqlField | string | SqlFn | SqlLiteral | SqlSubQuery,
    public op: string,
    public right: any
  ) { }

  and(e: any) { return new SqlExpChain("AND", [this, e]); }
  or(e: any) { return new SqlExpChain("OR", [this, e]); }
}

export class SqlExpChain {
  constructor(
    public logic: "AND" | "OR",
    public exps: (SqlExp | SqlExpChain)[]
  ) { }

  and(e: any) { return new SqlExpChain("AND", [...this.exps, e]); }
  or(e: any) { return new SqlExpChain("OR", [...this.exps, e]); }
}

// ============================================================
// SUBQUERY BUILDER (V40 NORMALIZED)
// ============================================================
export class SqlSubQueryBuilder {
  private _selects: any[] = [];
  private _from: any = null;
  private _where: any[] = [];
  private _group: any[] = [];
  private _order: any[] = [];
  private _limit: number | null = null;
  private _offset: number | null = null;

  private row(alias: string) {
    return new Proxy({}, {
      get: (_, prop: string) => new SqlField(alias, prop)
    });
  }

  select(fn: any) { this._selects.push(fn); return this; }

  fromTable(fn: any, alias: string) {
    this._from = { table: fn(this.row(alias)), alias };
    return this;
  }

  fromCTE(name: string, alias: string) {
    this._from = { type: "CTE", name, alias };
    return this;
  }

  where(fn: any) { this._where.push(fn(this.row("sq"))); return this; }
  groupBy(fn: any) { this._group = fn(this.row("sq")); return this; }
  orderBy(fn: any) { this._order = fn(this.row("sq")); return this; }
  limit(n: number) { this._limit = n; return this; }
  offset(n: number) { this._offset = n; return this; }

  build() {
    const fromAlias: string = this._from?.alias ?? "sq";

    const normalizeSelects = (items: any[]): any[] => {
      const result: any[] = [];

      for (const fn of items ?? []) {
        if (typeof fn !== "function") continue;
        const row = this.row("sq");
        const obj = fn(row);

        if (obj instanceof SqlField) {
          result.push({
            columnName: `[${fromAlias}].[${obj.name}]`,
            alias: obj.name,
            isTrusted: true
          });
          continue;
        }

        if (obj instanceof SqlFn && obj.field instanceof SqlField) {
          const col = obj.field.name;
          result.push({
            raw: `${obj.op.toUpperCase()}([${fromAlias}].[${col}])`,
            alias: col,
            isTrusted: false
          });
          continue;
        }

        if (obj && typeof obj === "object") {
          for (const alias of Object.keys(obj)) {
            const v = obj[alias];
            if (v instanceof SqlField) {
              result.push({
                columnName: `[${fromAlias}].[${v.name}]`,
                alias,
                isTrusted: true
              });
            } else if (v instanceof SqlFn && v.field instanceof SqlField) {
              const col = v.field.name;
              result.push({
                raw: `${v.op.toUpperCase()}([${fromAlias}].[${col}])`,
                alias,
                isTrusted: false
              });
            }
          }
        }
      }

      return result.length
        ? result
        : [{ columnName: 1, alias: "x", isTrusted: true }];
    };

    return {
      type: "SubQuery",
      selects: normalizeSelects(this._selects),
      from: this._from,
      where: this._where,
      group: this._group,
      order: this._order,
      limit: this._limit,
      offset: this._offset
    };
  }
}

export class SqlSubQuery {
  constructor(public builder: (b: SqlSubQueryBuilder) => any) { }

  eq(v: any) { return new SqlExp(this, "=", v); }
  ne(v: any) { return new SqlExp(this, "<>", v); }
  gt(v: any) { return new SqlExp(this, ">", v); }
  gte(v: any) { return new SqlExp(this, ">=", v); }
  lt(v: any) { return new SqlExp(this, "<", v); }
  lte(v: any) { return new SqlExp(this, "<=", v); }
  in(v: any[]) { return new SqlExp(this, "IN", v); }
  notIn(v: any[]) { return new SqlExp(this, "NOT IN", v); }
}

// ============================================================
// APPLY
// ============================================================
export class SqlApply {
  constructor(public name: string, public builderFn: Function) { }
}

export class SqlApplyColumn {
  constructor(public name: string, public fieldFn: Function) { }

  asc() {
    const json = JSON.stringify({ type: "ApplyColumn", name: this.name });
    return `${json} ASC`;
  }

  desc() {
    const json = JSON.stringify({ type: "ApplyColumn", name: this.name });
    return `${json} DESC`;
  }
}

// ============================================================
// CTE BUILDER
// ============================================================
export class SqlCteBuilder {
  private _selects: any[] = [];
  private _from: any = null;
  private _where: any[] = [];
  private _group: any[] = [];
  private _order: any[] = [];

  constructor(public name: string) { }

  private row(alias: string) {
    return new Proxy({}, {
      get: (_, p: string) => new SqlField(alias, p)
    });
  }

  select(fn: any) { this._selects.push(fn); return this; }

  fromTable(fn: any, alias: string) {
    this._from = { table: fn(this.row(alias)), alias };
    return this;
  }

  where(fn: any) { this._where.push(fn(this.row("cte"))); return this; }
  groupBy(fn: any) { this._group = fn(this.row("cte")); return this; }
  orderBy(fn: any) { this._order = fn(this.row("cte")); return this; }

  build() {
    return {
      type: "CTE",
      name: this.name,
      selects: this._selects,
      from: this._from,
      where: this._where,
      group: this._group,
      order: this._order
    };
  }
}

// ============================================================
// RECURSIVE CTE BUILDER
// ============================================================
export class SqlRecursiveCteBuilder {
  public anchorPart: any = null;
  public recursivePart: any = null;
  public unionAll = true;

  constructor(public name: string) { }

  anchor(fn: (b: SqlCteBuilder) => any) {
    const b = new SqlCteBuilder(`${this.name}_anchor`);
    this.anchorPart = fn(b).build();
    return this;
  }

  recursive(fn: any) { fn(this); return this; }

  select(fn: any) {
    this.recursivePart = this.recursivePart ?? { selects: [], where: [], from: null };
    this.recursivePart.selects.push(fn);
    return this;
  }

  fromTable(fn: any, alias: string) {
    this.recursivePart = this.recursivePart ?? {};
    this.recursivePart.from = { table: fn({}), alias };
    return this;
  }

  where(fn: any) {
    this.recursivePart = this.recursivePart ?? { selects: [], where: [], from: null };
    this.recursivePart.where.push(fn({}));
    return this;
  }

  joinCTE(name: string, alias: string, fn: any) {
    const l = new Proxy({}, { get: (_: any, p: string) => new SqlField("cte", p) });
    const r = new Proxy({}, { get: (_: any, p: string) => new SqlField(alias, p) });

    const exp = fn(l, r);

    this.recursivePart = this.recursivePart ?? {};
    this.recursivePart.from = {
      type: "CTEJoin",
      table: name,
      alias,
      on: exp,
      isCteJoin: true
    };

    return this;
  }

  build() {
    return {
      type: "RecursiveCTE",
      name: this.name,
      anchor: this.anchorPart,
      recursive: this.recursivePart,
      unionAll: this.unionAll
    };
  }
}
